/**
 * @module features/client-device-management/api/wifiApi
 * @description API fetchers and TanStack Query hooks for Organization WiFi Network
 * Vault profiles, single-active device WiFi bindings, audited AES-256 password reveal,
 * and immutable transition audit logs. Never logs or caches decrypted secrets.
 */
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import {
  CreateWifiVaultFormValues,
  CustomDeviceWifiFormValues,
  DeviceWifiProfile,
  DeviceWifiProfileSchema,
  RevealedPasswordSchema,
  UpdateDeviceWifiInput,
  UpdateWifiVaultFormValues,
  WifiAuditItem,
  WifiAuditItemSchema,
  WifiVaultItem,
  WifiVaultItemSchema,
} from "../types";
import { clientDeviceKeys, parsePayloadOrThrow } from "./assignmentApi";

const BASE = "/super-admin/client-devices";

/**
 * Lists all active reusable WiFi profiles in a client organization's network vault.
 */
export async function getOrgWifiVault(clientId: string): Promise<WifiVaultItem[]> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/wifi-networks/`
    );
    return parsePayloadOrThrow(
      z.array(WifiVaultItemSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load organization WiFi vault.");
  }
}

/**
 * Creates a reusable WiFi profile in the organization's network vault (encrypted at rest).
 */
export async function createOrgWifiNetwork(
  clientId: string,
  input: CreateWifiVaultFormValues
): Promise<WifiVaultItem> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/wifi-networks/`,
      input
    );
    return parsePayloadOrThrow(
      WifiVaultItemSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to save WiFi profile to vault.");
  }
}

/**
 * Updates an existing WiFi profile in the organization's network vault.
 * When `password` is blank/omitted, the existing AES-256 encrypted passphrase is preserved.
 */
export async function updateOrgWifiNetwork(
  clientId: string,
  networkId: string,
  input: UpdateWifiVaultFormValues
): Promise<WifiVaultItem> {
  try {
    const payload: Record<string, string> = {
      network_name: input.network_name,
      ssid_name: input.ssid_name,
      notes: input.notes ?? "",
    };
    if (input.password && input.password.length > 0) {
      payload.password = input.password;
    }
    const res = await apiClient.put<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/wifi-networks/${networkId}/`,
      payload
    );
    return parsePayloadOrThrow(
      WifiVaultItemSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to update WiFi vault profile.");
  }
}

/**
 * Requests an audited decryption reveal of an organization vault WiFi passphrase.
 * Exposed strictly as a mutation so plaintext passphrases never persist in global query cache.
 */
export async function revealOrgWifiNetworkPassword(
  clientId: string,
  networkId: string
): Promise<string> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/wifi-networks/${networkId}/reveal/`
    );
    const parsed = parsePayloadOrThrow(
      RevealedPasswordSchema,
      res.data?.data,
      res.data?.request_id
    );
    return parsed.plaintext_password;
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to reveal vault WiFi passphrase.");
  }
}

/**
 * Deactivates a WiFi profile from the organization's network vault and detaches bound devices.
 */
export async function deleteOrgWifiNetwork(
  clientId: string,
  networkId: string
): Promise<void> {
  try {
    await apiClient.delete(
      `${BASE}/clients/${clientId}/wifi-networks/${networkId}/`
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to remove WiFi profile from vault.");
  }
}

/**
 * Retrieves the single active WiFi profile bound to an assigned device (or null if none).
 */
export async function getDeviceWifiProfile(
  assignmentId: string
): Promise<DeviceWifiProfile | null> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-profile/`
    );
    const payload = res.data?.data;
    // Why: Backend returns `{}` when `data` is None in `success_response(data=None)`
    // or null if explicitly serialized; handle empty object as no active profile.
    if (
      !payload ||
      (typeof payload === "object" && Object.keys(payload as object).length === 0)
    ) {
      return null;
    }
    return parsePayloadOrThrow(
      DeviceWifiProfileSchema,
      payload,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load device WiFi binding.");
  }
}

/**
 * Binds an organization vault WiFi profile as the device's sole active WiFi profile.
 */
export async function assignVaultWifiToDevice(
  assignmentId: string,
  input: { client_wifi_network_id: string; notes?: string }
): Promise<DeviceWifiProfile> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-profile/`,
      input
    );
    return parsePayloadOrThrow(
      DeviceWifiProfileSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to attach vault WiFi profile to device.");
  }
}

/**
 * Configures a device-specific custom WiFi profile override (AES-256 encrypted at rest).
 */
export async function setCustomDeviceWifi(
  assignmentId: string,
  input: CustomDeviceWifiFormValues
): Promise<DeviceWifiProfile> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-profile/custom/`,
      input
    );
    return parsePayloadOrThrow(
      DeviceWifiProfileSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to configure custom WiFi profile.");
  }
}

/**
 * Updates the active WiFi binding on an assigned device in-place (preserving existing
 * encrypted custom passphrase when `custom_password` is blank/omitted) and records an
 * immutable WiFi audit entry.
 */
export async function updateDeviceWifiProfile(
  assignmentId: string,
  input: UpdateDeviceWifiInput
): Promise<DeviceWifiProfile> {
  try {
    const payload: UpdateDeviceWifiInput = { ...input };
    if (!payload.custom_password) {
      delete payload.custom_password;
    }
    const res = await apiClient.put<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-profile/`,
      payload
    );
    return parsePayloadOrThrow(
      DeviceWifiProfileSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to update active device WiFi details.");
  }
}

/**
 * Detaches the active WiFi profile from an assigned device.
 */
export async function removeDeviceWifi(assignmentId: string): Promise<void> {
  try {
    await apiClient.delete(`${BASE}/${assignmentId}/wifi-profile/`);
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to detach WiFi profile from device.");
  }
}

/**
 * Requests an audited decryption reveal of the active WiFi passphrase for an assigned device.
 * Intentionally exposed as a mutation (not a cached query) so plaintext secrets never sit in
 * the global TanStack Query cache.
 */
export async function revealDeviceWifiPassword(
  assignmentId: string
): Promise<string> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-profile/reveal/`
    );
    const parsed = parsePayloadOrThrow(
      RevealedPasswordSchema,
      res.data?.data,
      res.data?.request_id
    );
    return parsed.plaintext_password;
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to reveal WiFi passphrase.");
  }
}

/**
 * Retrieves the immutable chronological WiFi assignment audit log for an assigned device.
 */
export async function getDeviceWifiAuditLogs(
  assignmentId: string
): Promise<WifiAuditItem[]> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/wifi-audit-logs/`
    );
    return parsePayloadOrThrow(
      z.array(WifiAuditItemSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load WiFi transition audit logs.");
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

export function useOrgWifiVault(clientId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: clientDeviceKeys.wifiVault(clientId ?? ""),
    queryFn: () => getOrgWifiVault(clientId!),
    enabled: Boolean(clientId) && enabled,
  });
}

export function useCreateOrgWifiNetwork(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWifiVaultFormValues) =>
      createOrgWifiNetwork(clientId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiVault(clientId),
      });
    },
  });
}

export function useUpdateOrgWifiNetwork(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      networkId,
      input,
    }: {
      networkId: string;
      input: UpdateWifiVaultFormValues;
    }) => updateOrgWifiNetwork(clientId, networkId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiVault(clientId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useRevealOrgWifiNetworkPassword(clientId: string) {
  return useMutation({
    mutationFn: (networkId: string) =>
      revealOrgWifiNetworkPassword(clientId, networkId),
  });
}

export function useDeleteOrgWifiNetwork(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (networkId: string) => deleteOrgWifiNetwork(clientId, networkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiVault(clientId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useDeviceWifiProfile(
  assignmentId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: clientDeviceKeys.deviceWifi(assignmentId ?? ""),
    queryFn: () => getDeviceWifiProfile(assignmentId!),
    enabled: Boolean(assignmentId) && enabled,
  });
}

export function useDeviceWifiAuditLogs(
  assignmentId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: clientDeviceKeys.wifiAudit(assignmentId ?? ""),
    queryFn: () => getDeviceWifiAuditLogs(assignmentId!),
    enabled: Boolean(assignmentId) && enabled,
  });
}

export function useAssignVaultWifi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      client_wifi_network_id,
      notes,
    }: {
      assignmentId: string;
      client_wifi_network_id: string;
      notes?: string;
    }) =>
      assignVaultWifiToDevice(assignmentId, { client_wifi_network_id, notes }),
    onSuccess: (_, { assignmentId }) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.deviceWifi(assignmentId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiAudit(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useSetCustomDeviceWifi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: CustomDeviceWifiFormValues;
    }) => setCustomDeviceWifi(assignmentId, input),
    onSuccess: (_, { assignmentId }) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.deviceWifi(assignmentId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiAudit(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useUpdateDeviceWifiProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: UpdateDeviceWifiInput;
    }) => updateDeviceWifiProfile(assignmentId, input),
    onSuccess: (_, { assignmentId }) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.deviceWifi(assignmentId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiAudit(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useRemoveDeviceWifi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => removeDeviceWifi(assignmentId),
    onSuccess: (_, assignmentId) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.deviceWifi(assignmentId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.wifiAudit(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

export function useRevealDeviceWifiPassword() {
  return useMutation({
    mutationFn: (assignmentId: string) => revealDeviceWifiPassword(assignmentId),
  });
}

